import { memo } from "react";
import ToggleButton from "@mui/material/ToggleButton";
import Stack from "@mui/material/Stack";

import {
  isCommunicationsFocusMode,
  type PatientCommunicationRow,
} from "../aggregations";
import type {
  CommunicationsFocusMode,
  CrmPatientSummary,
  FluxZapCommunicationRecord,
  TemplateSendAggregateRow,
} from "../types";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import CommunicationsModelsFocusPanel from "./CommunicationsModelsFocusPanel";
import CommunicationsPatientsFocusPanel from "./CommunicationsPatientsFocusPanel";
import CommunicationsSegmentedFilterControl from "./CommunicationsSegmentedFilterControl";
import CommunicationsSubsectionPanel from "./CommunicationsSubsectionPanel";
import { BarChart } from "@mui/icons-material";

export interface CommunicationsFocusPanelProps {
  focusMode: CommunicationsFocusMode;
  onFocusModeChange: (mode: CommunicationsFocusMode) => void;
  templateRows: TemplateSendAggregateRow[];
  selectedTemplateCode: string | null;
  onSelectTemplateCode: (code: string | null) => void;
  selectedTemplateMessages: FluxZapCommunicationRecord[];
  resolvePatientForMessage: (
    msg: FluxZapCommunicationRecord,
  ) => CrmPatientSummary | undefined;
  patientMessageGroups: PatientCommunicationRow[];
  vetApp?: boolean;
  petNamesByTutorLogicalId?: Map<string, string[]>;
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

/**
 * Subseção Foco da visualização: toggle modelos/pacientes + painel correspondente.
 */
const CommunicationsFocusPanel = memo(
  ({
    focusMode,
    onFocusModeChange,
    templateRows,
    selectedTemplateCode,
    onSelectTemplateCode,
    selectedTemplateMessages,
    resolvePatientForMessage,
    patientMessageGroups,
    vetApp = false,
    petNamesByTutorLogicalId,
    onOpenSendMessage,
  }: CommunicationsFocusPanelProps) => (
    <CommunicationsSubsectionPanel
      panelId="communications-focus"
      defaultExpanded
      icon={<BarChart />}
      title="Visão Geral"
      subtitle="Analise envios por modelo de mensagem ou por paciente."
    >
      <Stack spacing={1.5}>
        <CommunicationsSegmentedFilterControl
          exclusive
          value={focusMode}
          onChange={(_, value) => {
            if (value != null && isCommunicationsFocusMode(value)) {
              onFocusModeChange(value);
            }
          }}
          aria-label="Alternar foco entre modelos de mensagem e pacientes"
        >
          <ToggleButton value="patients" aria-label="Foco em pacientes">
            Pacientes
          </ToggleButton>
          <ToggleButton value="templates" aria-label="Foco em modelos">
            Modelos de mensagem
          </ToggleButton>
        </CommunicationsSegmentedFilterControl>

        {focusMode === "templates" ? (
          <CommunicationsModelsFocusPanel
            templateRows={templateRows}
            selectedTemplateCode={selectedTemplateCode}
            onSelectTemplateCode={onSelectTemplateCode}
            selectedTemplateMessages={selectedTemplateMessages}
            vetApp={vetApp}
            petNamesByTutorLogicalId={petNamesByTutorLogicalId}
            resolvePatientForMessage={resolvePatientForMessage}
            onOpenSendMessage={onOpenSendMessage}
          />
        ) : (
          <CommunicationsPatientsFocusPanel
            patientMessageGroups={patientMessageGroups}
            vetApp={vetApp}
            petNamesByTutorLogicalId={petNamesByTutorLogicalId}
            onOpenSendMessage={onOpenSendMessage}
          />
        )}
      </Stack>
    </CommunicationsSubsectionPanel>
  ),
);

CommunicationsFocusPanel.displayName = "CommunicationsFocusPanel";

export default CommunicationsFocusPanel;
